import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useDemo } from '../../contexts/DemoContext';
import {
  getGoalById,
  updateGoals,
  completeInitialReview
} from '../../services/goalsService';
import {
  isDemoGoalId,
  getDemoGoalById,
  updateDemoGoalGoals,
  completeDemoInitialReview
} from '../../services/demoData';
import { getAllManagers } from '../../services/userService';
import { GoalDocument, Goal, GoalType } from '../../types';
import {
  ArrowLeft,
  CheckCircle,
  Save,
  AlertCircle,
  Edit3,
  Eye,
  FlaskConical
} from 'lucide-react';

const goalTypeLabels: Record<GoalType, string> = {
  normal: 'Normal Goal',
  stretch: 'Stretch Goal',
  hard: 'Hard Goal',
};

const goalTypeColors: Record<GoalType, string> = {
  normal: 'bg-blue-100 text-blue-700',
  stretch: 'bg-yellow-100 text-yellow-700',
  hard: 'bg-red-100 text-red-700',
};

const ReviewGoal = () => {
  const { goalId } = useParams<{ goalId: string }>();
  const { user, isDeveloper } = useAuth();
  const { isDemoMode, refreshDemo } = useDemo();
  const navigate = useNavigate();
  const [goal, setGoal] = useState<GoalDocument | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [canImpersonate, setCanImpersonate] = useState(false);

  // Check if this is a demo goal
  const isDemo = goalId ? isDemoGoalId(goalId) : false;

  // Check if user has canImpersonate permission
  useEffect(() => {
    const checkImpersonatePermission = async () => {
      if (!user?.email) {
        setPermissionLoading(false);
        return;
      }

      // Developers don't need the canImpersonate flag
      if (isDeveloper) {
        setPermissionLoading(false);
        return;
      }

      // In demo mode, always allow impersonation
      if (isDemoMode || isDemo) {
        setCanImpersonate(true);
        setPermissionLoading(false);
        return;
      }

      try {
        const managers = await getAllManagers();
        const currentUser = managers.find(m => m.email === user.email);
        if (currentUser?.canImpersonate) {
          setCanImpersonate(true);
        }
      } catch (error) {
        console.error('Error checking impersonate permission:', error);
      } finally {
        setPermissionLoading(false);
      }
    };

    checkImpersonatePermission();
  }, [user?.email, isDeveloper, isDemoMode, isDemo]);

  useEffect(() => {
    const fetchGoal = async () => {
      if (!goalId) return;

      try {
        // Check if this is a demo goal
        if (isDemo) {
          const demoGoal = getDemoGoalById(goalId);
          setGoal(demoGoal);
          if (demoGoal) {
            setGoals(demoGoal.goals);
          }
        } else {
          const goalDoc = await getGoalById(goalId);
          setGoal(goalDoc);
          if (goalDoc) {
            setGoals(goalDoc.goals);
          }
        }
      } catch (error) {
        console.error('Error fetching goal:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGoal();
  }, [goalId, isDemo]);

  const updateGoal = (index: number, field: keyof Goal, value: string) => {
    const newGoals = [...goals];
    newGoals[index] = { ...newGoals[index], [field]: value };
    setGoals(newGoals);
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!goalId || !goal) return;

    setSaving(true);
    try {
      if (isDemo) {
        // Demo mode - update in memory
        updateDemoGoalGoals(goalId, goals);
        refreshDemo();
        const updated = getDemoGoalById(goalId);
        setGoal(updated);
        if (updated) setGoals(updated.goals);
      } else {
        await updateGoals(goalId, goals);
        const updated = await getGoalById(goalId);
        setGoal(updated);
        if (updated) setGoals(updated.goals);
      }
      setHasChanges(false);
      setEditMode(false);
    } catch (error) {
      console.error('Error saving changes:', error);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveAll = async () => {
    if (!goalId || !goal || !user) return;

    // Validate all goals have titles and descriptions
    for (let i = 0; i < goals.length; i++) {
      if (!goals[i].title.trim()) {
        setError(`Goal ${i + 1} needs a title`);
        return;
      }
      if (!goals[i].description.trim()) {
        setError(`Goal ${i + 1} needs a description`);
        return;
      }
    }

    setSaving(true);
    setError(null);

    try {
      // Determine signer name
      const signerName = (canImpersonate && goal.managerEmail !== user.email)
        ? (goal.managerName || goal.managerEmail)
        : user.displayName;

      if (isDemo) {
        // Demo mode - update in memory
        if (hasChanges) {
          updateDemoGoalGoals(goalId, goals);
        }
        completeDemoInitialReview(goalId, signerName);
        refreshDemo();
      } else {
        // Save any pending changes first
        if (hasChanges) {
          await updateGoals(goalId, goals);
        }
        await completeInitialReview(goalId, signerName);
      }
      navigate('/manager');
    } catch (error) {
      console.error('Error completing review:', error);
      setError('Failed to approve goals. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // User can act as manager if they are the actual manager, a developer, or have canImpersonate permission
  // In demo mode, always allow
  const isManager = isDemo || isDemoMode || user?.email === goal?.managerEmail || isDeveloper || canImpersonate;

  if (loading || permissionLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Goal not found</p>
        <button
          onClick={() => navigate('/manager')}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          Go back
        </button>
      </div>
    );
  }

  if (!isManager) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You don't have permission to review this goal.</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Demo indicator */}
      {isDemo && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800">
          <FlaskConical className="w-5 h-5" />
          <span className="font-medium">Demo Goal</span>
          <span className="text-sm">- Changes are for training purposes only</span>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/manager')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Review Goals</h1>
            <p className="text-gray-500">{goal.userName} • Q{goal.quarter} {goal.year}</p>
          </div>
        </div>

        <button
          onClick={() => setEditMode(!editMode)}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            editMode
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {editMode ? (
            <>
              <Eye className="w-4 h-4" />
              View Mode
            </>
          ) : (
            <>
              <Edit3 className="w-4 h-4" />
              Edit Mode
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {editMode && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-700 text-sm">
            <strong>Edit Mode:</strong> You can modify the employee's goals below. Changes will be saved when you click "Save Changes" or "Approve All Goals".
          </p>
        </div>
      )}

      <div className="space-y-4">
        {goals.map((g, index) => (
          <div
            key={index}
            className="bg-white rounded-xl shadow-sm p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xl font-bold text-gray-300">#{index + 1}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${goalTypeColors[g.type]}`}>
                {goalTypeLabels[g.type]}
              </span>
            </div>

            {editMode ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Goal Title
                  </label>
                  <input
                    type="text"
                    value={g.title}
                    onChange={(e) => updateGoal(index, 'title', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={g.description}
                    onChange={(e) => updateGoal(index, 'description', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>
              </div>
            ) : (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">{g.title || 'No title'}</h3>
                <p className="text-gray-600">{g.description || 'No description'}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-between">
        <button
          onClick={() => navigate('/manager')}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>

        <div className="flex gap-4">
          {hasChanges && (
            <button
              onClick={handleSaveChanges}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          )}

          <button
            onClick={handleApproveAll}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Approve All Goals
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewGoal;